import React from "react";
import { Close, Copy, User } from "@payping/icons";
import { Button, Card, Col, Drawer, Flex, Grid, Row, Typography } from "@payping/web-components";
import { reportApis, userApis } from "@payping/api";
import { useAsync } from "react-use";

import { useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { referralCopyCodeId, referralLinkId } from "@/assets/googleTrackIds";
import Chart from "./Chart";
import ActivateReferral from "./ReferralModal/ActivateReferral";
import { useNavigate } from "react-router-dom";
import { DashboardRoutes } from "@/router";

const Referral: React.FC = () => {
  const { sm, md } = Grid.useBreakpoint();
  const { t } = useAppTranslation();

  const isReferralActive = useAppSelector((state) => state.user.isReferralActive);
  const { referralCode } = useAppSelector((state) => ({
    referralCode: state.user?.referralCode,
  }));
  const navigate = useNavigate();

  // const { navigateToLast } = useLastUrlWasPage();

  // =========================================
  // Handler
  // =========================================
  const { value: countValue } = useAsync(userApis.referralCountWithCancellationTokenGet, []);
  const { value: amountValue } = useAsync(reportApis.reportGetReferralAmounts, []);
  const { value: revenueValue } = useAsync(reportApis.reportGetDailyReferralRevenue, []);

  // =========================================
  // Render
  // =========================================
  return (
    <Drawer
      open
      title={t("upgrade.referral")}
      height={"100%"}
      placement={"bottom"}
      closeIcon={false}
      bodyStyle={{ padding: 0 }}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            navigate(DashboardRoutes.home.overview.url);
          }}
        />
      }
    >
      <div className={"!tw-mb-52"}>
        {isReferralActive ? (
          <Row justify={"center"} gutter={[0, 32]} className={"tw-pt-12"}>
            {/* UP SECTION */}
            <Col xs={24} md={14}>
              <Card.Ant>
                <Row gutter={[0, 16]}>
                  {/* UpRight Section */}
                  <Col xs={24} md={12}>
                    <Flex vertical gap={16}>
                      {/* Referral Link */}
                      <Flex justify={"space-between"}>
                        <Typography.Text type={"secondary"}>{t("referral.referralLink")}</Typography.Text>
                        <Typography.Text
                          className={"text--cta"}
                          copyable={{
                            icon: [
                              <Copy
                                data-google-track={referralLinkId}
                                className={"text--black"}
                                strokeWidth={"1.7px"}
                                key={"copy-icon"}
                                width={"14px"}
                              />,
                            ],
                          }}
                        >{`ppng.ir/r/${referralCode}`}</Typography.Text>
                      </Flex>

                      {/* Referral Code */}
                      <Flex justify={"space-between"}>
                        <Typography.Text type={"secondary"}>{t("referral.referralCode")}</Typography.Text>
                        <Typography.Text
                          className={"text--cta"}
                          copyable={{
                            icon: [
                              <Copy
                                data-google-track={referralCopyCodeId}
                                className={"text--black"}
                                strokeWidth={"1.7px"}
                                key={"copy-icon"}
                                width={"14px"}
                              />,
                            ],
                          }}
                        >
                          {referralCode}
                        </Typography.Text>
                      </Flex>
                    </Flex>
                  </Col>
                  {/* UpLeft Section */}
                  <Col xs={24} md={12}>
                    <Flex vertical justify={"center"} align={md ? "end" : "center"} gap={16}>
                      <Typography.Text type={"secondary"}>{t("referral.totalReferralUsers.title")}</Typography.Text>
                      <Flex align={"center"}>
                        <Typography.Text strong>{countValue}</Typography.Text>
                        <Typography.Text strong>{t("referral.totalReferralUsers.suffix")}</Typography.Text>
                        <User width={"32px"} className={"tw-mr-4"} />
                      </Flex>
                    </Flex>
                  </Col>
                </Row>
              </Card.Ant>
            </Col>

            {/* DOWN SECTION (Chart) */}
            <Col xs={24} md={14}>
              <Card.Ant>
                <Row gutter={[0, 16]}>
                  {/* Total Referral Price */}
                  <Col xs={24} sm={12}>
                    <Flex justify={sm ? "center" : "space-around"}>
                      <Typography.Text type={"secondary"} className={"ml-1"} strong>
                        {t("referral.totalReferralPrice.title")}
                      </Typography.Text>
                      <Typography.Text strong>
                        {typeof amountValue?.balance === "number" ? amountValue?.balance : 0}
                      </Typography.Text>
                    </Flex>
                  </Col>

                  {/* Total Settlement Amount */}
                  <Col xs={24} sm={12}>
                    <Flex justify={sm ? "center" : "space-around"}>
                      <Typography.Text type={"secondary"} className={"ml-1"} strong>
                        {t("referral.totalSettlementAmount.title")}
                      </Typography.Text>
                      <Typography.Text strong>
                        {typeof amountValue?.totalAmount === "number" ? amountValue?.totalAmount : 0}
                      </Typography.Text>
                    </Flex>
                  </Col>

                  {/* Chart */}
                  <Col span={24}>
                    <Chart revenueValue={revenueValue} />
                  </Col>
                </Row>
              </Card.Ant>
            </Col>
          </Row>
        ) : (
          <ActivateReferral />
        )}
      </div>
    </Drawer>
  );
};

export default Referral;
